import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { CheckSquare, FileText, User, ArrowRight } from 'lucide-react';
import { useUkstrStore } from '../data/ukstrStore';
import { useStore } from '../data/store';
import { toast } from 'sonner';
export default function UkstrAnswers() {
  const navigate = useNavigate();
  const {
    answers,
    tasks,
    updateAnswerStatus
  } = useUkstrStore();
  const createReport = useStore(s => s.createReport);
  const submitReport = useStore(s => s.submitReport);
  const handleVerify = (id: string) => {
    updateAnswerStatus(id, 'Diverifikasi');
    toast.success('Jawaban berhasil diverifikasi.');
  };
  // Bridge UK/STR → Intelligence Report Studio. Promoting a verified field
  // answer creates a real report in the main reports store (seeded with the
  // field material) and routes the analyst straight into the report studio,
  // closing the end-to-end UK/STR → laporan intelijen loop.
  const handleUseInReport = (id: string) => {
    const ans = answers.find(a => a.id === id);
    if (!ans) return;
    const task = tasks.find(t => t.id === ans.taskId);
    const report = createReport({
      title: task ? `Laporan Hasil UK/STR — ${task.judul}` : `Laporan Hasil UK/STR — ${ans.taskId}`,
      type: 'Informasi',
      category: 'Intelijen Strategis',
      urgency: task?.klasifikasi === 'SANGAT RAHASIA' ? 'Tinggi' : 'Sedang',
      region: ans.wilayah,
      sender: `${ans.personelName} (${ans.personelId})`,
      binda: `BINDA ${ans.wilayah}`,
      ringkasan: ans.ringkasan,
      kronologi: ans.kronologi,
      pihakTerkait: ans.pihakTerkait,
      potensiDampak: ans.catatan,
      description: ans.uraian,
      attachments: ans.lampiran.map(name => ({
        name,
        kind: 'foto' as const
      }))
    });
    // Move it into the verification pipeline so the report studio's staged
    // validation rail is active immediately.
    submitReport(report.id);
    updateAnswerStatus(id, 'Digunakan dalam Laporan');
    toast.success('Jawaban diangkat menjadi Laporan Intelijen. Membuka Studio…');
    navigate(`/reports/${report.id}/analysis`);
  };
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-3856">
      <div data-id="element-3857">
        <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground uppercase" data-id="element-3858">
          Jawaban Lapangan
        </h1>
        <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-3859">
          Verifikasi & Seleksi Bahan Keterangan UK/STR
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4" data-id="element-3860">
        {answers.map(ans => <Card key={ans.id} className="glass-panel border-border/50" data-id="element-3861">
            <CardHeader className="p-4 border-b border-border/50 bg-card/50 flex flex-row justify-between items-center" data-id="element-3862">
              <div className="flex items-center gap-3" data-id="element-3863">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center" data-id="element-3864">
                  <User className="w-4 h-4 text-primary" data-id="element-3865" />
                </div>
                <div data-id="element-3866">
                  <CardTitle className="text-sm font-bold" data-id="element-3867">
                    {ans.personelName}
                  </CardTitle>
                  <div className="text-[10px] font-mono text-muted-foreground" data-id="element-3868">
                    {ans.wilayah} • {ans.waktu} • Tugas: {ans.taskId}
                  </div>
                </div>
              </div>
              <Badge variant="outline" className="font-mono text-[10px] uppercase" data-id="element-3869">
                {ans.status}
              </Badge>
            </CardHeader>
            <CardContent className="p-4 space-y-4" data-id="element-3870">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono" data-id="element-3871">
                <div data-id="element-3872">
                  <span className="text-muted-foreground uppercase block mb-1" data-id="element-3873">
                    Ringkasan
                  </span>
                  <div className="bg-black/30 p-2 rounded border border-border/30" data-id="element-3874">
                    {ans.ringkasan}
                  </div>
                </div>
                <div data-id="element-3875">
                  <span className="text-muted-foreground uppercase block mb-1" data-id="element-3876">
                    Lokasi
                  </span>
                  <div className="bg-black/30 p-2 rounded border border-border/30" data-id="element-3877">
                    {ans.lokasi}
                  </div>
                </div>
                <div className="md:col-span-2" data-id="element-3878">
                  <span className="text-muted-foreground uppercase block mb-1" data-id="element-3879">
                    Uraian Lengkap
                  </span>
                  <div className="bg-black/30 p-2 rounded border border-border/30 whitespace-pre-wrap" data-id="element-3880">
                    {ans.uraian}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-border/30" data-id="element-3881">
                {ans.status === 'Dikirim' && <Button size="sm" onClick={() => handleVerify(ans.id)} className="bg-[var(--status-active)] text-black font-mono text-xs" data-id="element-3882">
                    <CheckSquare className="w-4 h-4 mr-2" data-id="element-3883" /> Verifikasi
                  </Button>}
                {ans.status === 'Diverifikasi' && <Button size="sm" onClick={() => handleUseInReport(ans.id)} className="bg-primary text-primary-foreground font-mono text-xs" data-id="element-3884">
                    <FileText className="w-4 h-4 mr-2" data-id="element-3885" /> Generate Laporan
                    Intelijen
                  </Button>}
                {ans.status === 'Digunakan dalam Laporan' && <Button size="sm" variant="outline" onClick={() => navigate('/reports')} className="font-mono text-xs" data-id="element-3886">
                    Lihat di Studio Laporan{' '}
                    <ArrowRight className="w-4 h-4 ml-2" data-id="element-3887" />
                  </Button>}
              </div>
            </CardContent>
          </Card>)}
        {answers.length === 0 && <div className="text-center py-12 text-muted-foreground font-mono text-xs border border-border/50 rounded glass-panel" data-id="element-3888">
            Belum ada jawaban lapangan yang masuk.
          </div>}
      </div>
    </div>;
}